import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { dashboardReady } from 'stash/actions/dashboard';
import { push } from 'connected-react-router';
import {
	getAccountAutoStash,
	getAccountBankAccounts,
	getAccountCards,
	getAccountPortfolio,
	getActivePlatformTier,
	getBookmarks,
	getExternalAccount,
	getFundingSources,
	getLaunchInfo,
} from 'stash/actions/api/';
import { ARCHIVE_RESTRICTED_REASONS } from 'stash/constants/mappings';
import { PERSONAL_BROKERAGE } from 'stash/constants/accountTypes';
import { hasClosedAllAccounts } from 'stash/utils/helpers';
import {
	authorizedContentMoatUrls,
	HOME,
	isAllowedToRenderWebApp,
	isStateZeroPending,
	LAUNCH_INFO,
	REG_CONTENT_HOME,
	REG_INITIAL,
	REG_INITIAL_SHORT,
	REG_STATE_ZERO,
	STATE_ZERO_OID,
	type LaunchInfoKey,
} from 'stash/constants/launchInfo';
import DuplicateAccountModal from 'stash/components/duplicateAccountModal';
import { logOut, logOutReasons } from 'stash/actions';
import { useLocation } from 'react-router-dom';
import { selectIsAccountlessAppEntryEnabled, selectIsHome } from 'stash/selectors';
import { useFetch } from '@stashinvest/use-fetch';
import useSendAnalytics from 'stash/utils/instrumentation/sendAnalytics';
import FriendlyLoader from 'stash/components/FriendlyLoader';
import { redirectWindowLocation } from 'stash/utils/windowNavigation';
import { datadogRum } from '@datadog/browser-rum';
import type { Entities } from 'stash/types/redux/entities';

type DashboardContainerProps = {
	children?: React.ReactNode;
};

type DashboardContainerState = {
	application: {
		dashboard?: boolean;
	};
	entities: Entities;
};

type LaunchInfoAccount = {
	id: string;
	type: string;
	state: string;
};

type LaunchInfoResponse = {
	accounts: LaunchInfoAccount[];
	location: string;
	user: {
		restricted_reason?: string | null;
		uuid?: string;
	};
};

type PxQuestionResponse = {
	question?: {
		answer?: {
			value?: string | null;
		};
	};
};

type ExternalAccountResponse = {
	manageExternalBankAccount?: {
		bankAccount?: unknown;
	};
};

type DashboardDispatch = (action: unknown) => Promise<unknown>;

const SKIP_DASHBOARD_INITIALIZATION = Symbol('skip-dashboard-initialization');

const DashboardContainer = ({ children }: DashboardContainerProps) => {
	const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
	const ready = useSelector(
		(state: DashboardContainerState) => state.application.dashboard
	);
	const access_token = useSelector(
		(state: DashboardContainerState) => state.entities.api_key?.access_token
	);
	const isHome = useSelector(selectIsHome);
	const isAccountlessAppEntryEnabled = useSelector(selectIsAccountlessAppEntryEnabled);
	const [accounts, setAccounts] = useState<LaunchInfoAccount[]>([]);
	const [finalPath, setFinalPath] = useState('');
	const dispatch = useDispatch<DashboardDispatch>();
	const location = useLocation();
	const { sendAnalyticData } = useSendAnalytics();

	// No error scenario for below API. If it fails then the app will continue and load Home.
	const { fetch: getPxQuestions } = useFetch({
		path: `/apis/customers/v1/questions/${STATE_ZERO_OID}`,
		onSuccess: ({ question }: PxQuestionResponse) => {
			if (
				!isAccountlessAppEntryEnabled &&
				isStateZeroPending(question?.answer?.value ?? null) &&
				!isAllowedToRenderWebApp()
			) {
				redirectToStateZero();
			}
		},
	});

	useEffect(() => {
		if (access_token) {
			getDashboardContent();
		} else {
			navigateToLogIn();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [access_token]);

	useEffect(() => {
		if (location.pathname === '/' && isHome && !isAccountlessAppEntryEnabled) {
			runStateZeroChecks();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location, isHome]);

	const getAccountContent = ({ id }: { id: string }) => {
		return [
			dispatch(getAccountPortfolio(id)).catch(() => null),
			dispatch(getAccountCards(id)).catch(() => null),
			dispatch(getAccountAutoStash(id)).catch(() => null),
			dispatch(getAccountBankAccounts(id)).catch(() => null),
		];
	};

	const prepareDashboard = (data: LaunchInfoAccount[]) => {
		const accountContent = data
			.map(getAccountContent)
			.reduce<ReturnType<typeof getAccountContent>>((acc, accountContent) => {
				return [...acc, ...accountContent];
			}, []);

		return Promise.all([
			dispatch(getFundingSources()).catch(() => null),
			dispatch(getActivePlatformTier()).catch(() => null),
			dispatch(getExternalAccount()).catch(() => null),
			...accountContent,
		]);
	};

	const onCloseModal = () => {
		setIsDuplicateModalOpen(false);
		dispatch(logOut(logOutReasons.DUPLICATE_LOGIN));
	};

	const requestBookmarks = () => {
		dispatch(getBookmarks()).catch((error: unknown) => {
			console.error(error);
		});
	};

	const onContinueClicked = () => {
		prepareDashboard(accounts)
			.then(() => {
				setIsDuplicateModalOpen(false);
				if (finalPath) {
					dispatch(push(finalPath));
				}
			})
			.catch((err: unknown) => {
				console.error(err);
				return err;
			})
			.then(() => {
				requestBookmarks();
				return dispatch(dashboardReady());
			});
	};

	const redirectToStateZero = () => {
		redirectWindowLocation(
			window.location.hostname === 'app-local.stash.com'
				? `https://app-local.stash.com:9009/sign-up/get-started`
				: LAUNCH_INFO[REG_STATE_ZERO].route
		);
	};

	const runStateZeroChecks = () => {
		dispatch(getExternalAccount()).then((res) => {
			const externalAccount = res as ExternalAccountResponse | undefined;
			if (!Boolean(externalAccount?.manageExternalBankAccount?.bankAccount)) {
				getPxQuestions();
			}
		});
	};

	const getDashboardContent = () => {
		return dispatch(getLaunchInfo())
			.then((res): unknown => {
				const launchInfo = res as LaunchInfoResponse | undefined;
				if (!launchInfo || !Array.isArray(launchInfo.accounts)) {
					throw new Error('Invalid response from getLaunchInfo');
				}
				const { location, user } = launchInfo;
				if (!location || !user) {
					throw new Error('Missing location or user in response from getLaunchInfo');
				}
				const { restricted_reason } = user;
				setAccounts(launchInfo.accounts);
				// Only used for checking for inactive state
				const investAccount = launchInfo.accounts.find(
					(acc) => acc.type === PERSONAL_BROKERAGE
				);

				const hasInactiveInvestAccount =
					investAccount && investAccount.state === 'inactive';

				const launchInfoEntry =
					location in LAUNCH_INFO
						? LAUNCH_INFO[location as LaunchInfoKey]
						: undefined;

				const showContentMoat =
					!!launchInfoEntry &&
					location === REG_CONTENT_HOME &&
					!isAccountlessAppEntryEnabled &&
					!authorizedContentMoatUrls[window.location.pathname];

				// A REG_CONTENT_HOME user who resolves to disabled is either genuinely
				// in control or lost the cookie web-reg was meant to write, which
				// otherwise reads as a routing bug. This tracks that cookie health,
				// not the routing outcome: whether the redirect below actually runs
				// depends on a cascade of restricted-account branches that this
				// signal deliberately does not try to mirror.
				if (location === REG_CONTENT_HOME && !isAccountlessAppEntryEnabled) {
					datadogRum.addAction('AccountlessAppEntry_ContentMoatBounce', {
						pathname: window.location.pathname,
					});
				}

				const showOnboarding = !!launchInfoEntry && location === REG_INITIAL;
				const showOnboarding2 = !!launchInfoEntry && location === REG_INITIAL_SHORT;
				const showHome = location === HOME;

				if (!showOnboarding && !showOnboarding2 && user?.uuid) {
					sendAnalyticData(user.uuid);
				}

				// Handle re-directs for closed / restricted / incomplete accounts / duplicate accounts
				if (restricted_reason === 'DUPLICATE_ACCOUNT') {
					setIsDuplicateModalOpen(true);
					if (showContentMoat) {
						setFinalPath(LAUNCH_INFO[REG_CONTENT_HOME].route);
					} else if (showOnboarding || showOnboarding2) {
						setFinalPath(LAUNCH_INFO[REG_INITIAL].route);
					} else if (showHome) {
						setFinalPath('/');
					}
					return SKIP_DASHBOARD_INITIALIZATION;
				} else if (
					!showContentMoat &&
					(hasClosedAllAccounts(launchInfo.accounts) ||
						(restricted_reason != null &&
							(
								ARCHIVE_RESTRICTED_REASONS as Record<string, boolean | undefined>
							)[restricted_reason]))
				) {
					if (window.location.pathname === '/plan-details') {
						dispatch(push('/archive/plan-details'));
						return SKIP_DASHBOARD_INITIALIZATION;
					}
					dispatch(push('/archive/documents'));
					return SKIP_DASHBOARD_INITIALIZATION;

					// Handle inactive personal brokerage accounts
				} else if (hasInactiveInvestAccount && restricted_reason === 'USER_LOCKED') {
					dispatch(push('/account-locked'));
					return SKIP_DASHBOARD_INITIALIZATION;
				} else if (
					hasInactiveInvestAccount &&
					restricted_reason &&
					restricted_reason !== 'DUPLICATE_ACCOUNT'
				) {
					dispatch(push('/reactivate-account'));
					return SKIP_DASHBOARD_INITIALIZATION;
				} else if (showContentMoat) {
					// Assigning location unloads the document and aborts any fetch
					// started afterwards. Safari reports that abort as "Load failed".
					redirectToStateZero();
					return SKIP_DASHBOARD_INITIALIZATION;
				} else if (showOnboarding) {
					redirectWindowLocation(LAUNCH_INFO[REG_INITIAL].route);
					return SKIP_DASHBOARD_INITIALIZATION;
				} else if (showOnboarding2) {
					redirectWindowLocation(LAUNCH_INFO[REG_INITIAL_SHORT].route);
					return SKIP_DASHBOARD_INITIALIZATION;
				}

				if (location === HOME && !isAccountlessAppEntryEnabled) {
					runStateZeroChecks();
				}
				return prepareDashboard(launchInfo.accounts);
			})
			.then((result: unknown) => {
				if (result === SKIP_DASHBOARD_INITIALIZATION) {
					return undefined;
				}
				requestBookmarks();
				return dispatch(dashboardReady());
			})
			.catch((err: unknown) => {
				return err;
			});
	};
	const navigateToLogIn = () => {
		return dispatch(push('/log-in'));
	};

	if (isDuplicateModalOpen) {
		return <DuplicateAccountModal onSubmit={onContinueClicked} onClose={onCloseModal} />;
	}

	return (
		<FriendlyLoader loading={!ready} loaderClassName="min-h-screen">
			{children}
		</FriendlyLoader>
	);
};

export default DashboardContainer;
